import { apiClient } from '../client';
import { apiConfig } from '@/config';

/**
 * Message list request
 */
export interface GetMessagesRequest {
  folder?: 'inbox' | 'sent' | 'archive';
  skipCount?: number;
  maxResultCount?: number;
  filter?: string;
}

/**
 * Message item
 */
export interface Message {
  id: string;
  subject: string;
  body: string;
  senderName: string;
  senderId: string;
  recipientNames: string[];
  creationTime: string;
  isRead: boolean;
  jobId?: string;
  jobStepId?: string;
  attachments?: MessageAttachment[];
}

export interface MessageAttachment {
  id: string;
  fileName: string;
  fileSize: number;
}

/**
 * Send message request
 */
export interface SendMessageRequest {
  recipientUserIds: string[];
  subject: string;
  body: string;
  jobId?: string;
  jobStepId?: string;
}

/**
 * Broadcast message request
 */
export interface BroadcastMessageRequest {
  workgroupIds: string[];
  subject: string;
  body: string;
}

/**
 * Paged response
 */
export interface PagedResponse<T> {
  totalCount: number;
  items: T[];
}

/**
 * Message API service
 */
export const messageService = {
  /**
   * Get paged message list
   */
  getMessages: async (params: GetMessagesRequest): Promise<PagedResponse<Message>> => {
    const { data } = await apiClient.get(apiConfig.endpoints.messagesPagedList, {
      params,
    });
    return data;
  },

  /**
   * Get unread message count for a specific user
   * Matches Angular: messageFor=userId, read=false
   */
  getUnreadCount: async (userId: string): Promise<number> => {
    const { data } = await apiClient.get(`${apiConfig.endpoints.messages}/count`, {
      params: {
        MessageFor: userId,
        Read: false,
      },
    });
    return data;
  },

  /**
   * Send a message
   */
  sendMessage: async (request: SendMessageRequest): Promise<Message> => {
    const { data } = await apiClient.post(apiConfig.endpoints.messages, request);
    return data;
  },

  /**
   * Broadcast message to workgroups
   */
  broadcastMessage: async (request: BroadcastMessageRequest): Promise<void> => {
    await apiClient.post(`${apiConfig.endpoints.messages}/broadcast-message`, request);
  },

  /**
   * Mark messages as read
   */
  markAsRead: async (messageIds: string[]): Promise<void> => {
    await apiClient.post(`${apiConfig.endpoints.messages}/mark-messages-read`, {
      messageIds,
    });
  },

  /**
   * Delete a message
   */
  deleteMessage: async (id: string): Promise<void> => {
    await apiClient.delete(`${apiConfig.endpoints.messages}/${id}`);
  },
};
